import { takeEvery, select, put, spawn, call } from 'redux-saga/effects';
import {
  ui as uiActions,
  user as userActions,
  flyers as flyersActions,
  stores as storesActions,
  router as routerActions,
  flyerGibs as flyerGibsActions,
  application as applicationActions,
  tags as tagsActions,
} from 'flux/actions';
import { LOCATION_CHANGE, replace } from 'connected-react-router';
import {
  application as applicationSelectors,
  flyers as flyersSelectors,
  ui as uiSelectors,
  user as userSelector,
  stores as storesSelectors,
  router as routerSelectors,
  property as propertySelectors,
  flyerGibs as flyerGibsSelectors,
} from 'flux/selectors';
import {
  fetchFlyerGibsResource,
  fetchFlyerGibsProductResource,
  fetchStoresResource,
  fetchFlyersResource,
  fetchFlyerResource,
  fetchStoreOpeningHoursResource,
} from 'resources';
import sortBy from 'lodash/sortBy';
import { flyerPath, flyersPath } from 'app/routes';
import {
  getTargetFlyerIdForDefaultVersion,
  mergeFlyerVersionResolution,
  mergeClientVersionLinksAndFlagsOntoFlyer,
  copyClientVersionExtrasFromPriorIfSamePromo,
  resolveFlyerVersionLinks,
  SKIP_DEFAULT_FLYER_VERSION_ALIGN_STATE_KEY,
} from 'utils/flyerVersion';
import sortFlyerGibIdsByFlyerOrder from 'utils/sortFlyerGibIdsByFlyerOrder';
import { v4 as uuidv4 } from 'uuid';
import { onRouterQueryUpdated } from './stores.sagas';
import { createLinkPath } from 'utils';
import { isSharedNextFlyer } from 'utils/openNextShare';

export function* updateFlyers() {
  const location = yield select(userSelector.selectNearbyGeolocation);
  const fetchedLocation = yield select(flyersSelectors.selectFetchedLocation);
  const storeCustomLabel = yield select(routerSelectors.selectStoreId);
  const flyerCustomLabel = yield select(routerSelectors.selectCustomLabel);

  if (
    !fetchedLocation ||
    (fetchedLocation.lat !== location.lat && fetchedLocation.lng !== location.lng)
  ) {
    try {
      yield put(applicationActions.setLoadingGeolocation(true));
      const response = yield call(fetchFlyersResource, {
        location,
        storeCustomLabel,
        flyerCustomLabel,
      });
      yield put(flyersActions.updateFlyersByLocation(response, location));
      yield put(storesActions.updateStoresByLocation(response));
    } catch (error) {
      yield put(flyersActions.updateFlyersByLocation({ list: {} }));
      yield put(storesActions.updateStoresByLocation({ list: {} }));
    } finally {
      yield put(applicationActions.setLoadingGeolocation(false));
    }

    const routerQuery = yield select(routerSelectors.selectRouterQuery);
    yield call(onRouterQueryUpdated, { payload: { query: routerQuery } });
  }
}

// TODO[GB] setNearbyFlyers non serve ma la sezione si da vedere
// ex checkNearbyFlyersChange function
export function* setSessionOnPropertty() {
  const isFlyersPath = yield select(routerSelectors.routerMatchPath, flyersPath);
  const isUserGeolocationMissing = yield select(applicationSelectors.isUserGeolocationMissing);

  // if (isFlyersPath && !isUserGeolocationMissing) {
  //   const nearbyFlyers = yield select(flyersSelectors.filterFlyersIdsByLocation);
  //   yield put(flyersActions.setNearbyFlyers(nearbyFlyers));
  // }

  const userSessionId = yield select(userSelector.selectUserSessionId);
  if (!userSessionId && !isUserGeolocationMissing && isFlyersPath) {
    const { id: propertyId } = yield select(propertySelectors.selectProperty);
    const sessionId = uuidv4();
    try {
      const sessionIdMapping = JSON.parse(window.sessionStorage.getItem('sessionIds')) || {};
      if (!sessionIdMapping[propertyId]) {
        sessionIdMapping[propertyId] = sessionId;
        window.sessionStorage.setItem('sessionIds', JSON.stringify(sessionIdMapping));
        yield put(userActions.setSessionId(sessionIdMapping[propertyId]));
      }
    } catch (e) {
      // Nothing happens
      yield put(userActions.setSessionId(sessionId));
    }
  }
}

// TODO[GB] rimuovere!
// export function* checkCurrentFlyerStores() {
//   const isStoresPath = yield select(routerSelectors.routerMatchPath, storesPath);
//   const { active: propertyStoreIsActive } = yield select(propertySelectors.selectPropertyExternals, 'store');
//   /* route related:
//     if is storesPath the dispatch is necessary for showing the stores list
//     if propertyStoreIsActive it is necessary for showing the number of stores on flyerPage
//   */

//   const currentFlyerId = yield select(flyersSelectors.selectCurrentFlyerId);
//   if ((isStoresPath || propertyStoreIsActive) && currentFlyerId) {
//     const storesList = yield select(flyersSelectors.filterCurrentFlyerStores);
//     if (storesList) {
//       yield put(flyersActions.setCurrentFlyerStores(storesList));
//     }
//   }
// }

export function* checkGeolocationAlert() {
  const isUserGeolocationMissing = yield select(applicationSelectors.isUserGeolocationMissing);
  const { geolocationAlert } = yield select(uiSelectors.selectBanners);
  if (isUserGeolocationMissing !== geolocationAlert.active) {
    yield put(uiActions.toggleBanner('geolocationAlert', isUserGeolocationMissing));
  }
}

export function* onSetGeolocation() {
  setGeolocationChanged = true;

  yield call(updateFlyers);
  yield spawn(setSessionOnPropertty);
  yield spawn(checkGeolocationAlert);
  // yield spawn(checkCurrentFlyerStores);
}

export function* onRouterUpdateGeolocation() {
  yield call(updateFlyers);
  yield spawn(setSessionOnPropertty);
}

function* onFetchCurrentFlyerStores(flyer, location) {
  const isCurrentFlyerSAType = yield select(flyersSelectors.isCurrentFlyerSAType);
  if (isCurrentFlyerSAType) return null;
  const epochTime = new Date().getTime();
  const storeCustomLabel = yield select(routerSelectors.selectStoreId);
  let stores = yield call(fetchStoresResource, {
    flyerId: flyer.id,
    location,
    epochTime,
    storeCustomLabel,
    parentFlyerId: flyer.settings.parent_id,
  });
  if (!stores || stores?.length === 0) {
    stores = flyer.stores ? Object.values(flyer.stores) : [];
  }
  const currentStoresList = yield select(storesSelectors.selectStores);
  const currentFlyerStoresList = [];

  stores?.forEach((store) => {
    const newStore = { ...store, ...currentStoresList[store.id] };
    currentStoresList[newStore.id] = newStore;
    currentFlyerStoresList.push(newStore);
  });

  yield put(storesActions.resetStores({ list: currentStoresList }));
  yield put(flyersActions.setCurrentFlyerStores(currentFlyerStoresList));
  // TODO[GB] non serve lo lascio come memo
  // if (currentStoresList[nearestStoreId]) {
  //   yield put(storesActions.setCurrentStore(nearestStoreId));
  // }
}

// export function* fetchFlyerStores(flyerId) {
//   const storesResource = yield fetchStoresResource(flyerId);
//   const currentStoresList = yield select(storesSelectors.selectStores);
//   const currentFlyerStoresList = [];
//   storesResource.forEach((store) => {
//     currentStoresList[store.id] = { ...currentStoresList[store.id], ...store };
//     currentFlyerStoresList.push(currentStoresList[store.id]);
//   });
//   yield put(storesActions.resetStores({ list: currentStoresList }));
//   yield put(flyersActions.setCurrentFlyerStores(currentFlyerStoresList));
// }

// export function* fetchFlyerContents(flyerId) {
//   yield spawn(fetchFlyerStores, flyerId);
//   yield spawn(fetchFlyerFlyerGibs, flyerId);
// }

// export function* fetchFlyerFlyerGibs(flyerId) {
//   const flyerGibsResource = yield fetchFlyerGibsResource(flyerId);
//   const flyerGibsList = { [flyerId]: Object.keys(flyerGibsResource) };
//   const flyerGibsChildIdsMap = getFlyerGibsChildIdsMap(flyerGibsResource);

//   yield put(
//     flyersActions.setCurrentFlyerGibs(flyerGibsList, flyerGibsChildIdsMap)
//   );
//   yield put(flyerGibsActions.resetFlyerGibs({ list: flyerGibsResource }));
// }

function getFlyerGibsChildIdsMap(flyerGibsResource) {
  const childCropsByParentId = {};

  for (const gibId in flyerGibsResource) {
    const gib = flyerGibsResource[gibId];
    const { type, parent_id } = gib;

    if (type === 'crop_child' && parent_id) {
      if (!childCropsByParentId[parent_id]) childCropsByParentId[parent_id] = [];
      childCropsByParentId[parent_id].push(gib);
    }
  }

  for (const gibId in childCropsByParentId) {
    childCropsByParentId[gibId] = sortBy(childCropsByParentId[gibId], [
      (gib) => gib.settings?.child?.order,
    ]);
  }

  const map = {};
  Object.entries(childCropsByParentId).forEach(([parentId, gibs]) => {
    map[parentId] = gibs.map((gib) => gib.id);
  });

  return map;
}

export function* onFetchFlyerGibs(flyer) {
  const isCurrentFlyerSAType = yield select(flyersSelectors.isCurrentFlyerSAType);

  if (!flyer?.id || isCurrentFlyerSAType) return null;

  const showFlyerGibs = yield select(propertySelectors.selectShowFlyerGibs);
  if (!showFlyerGibs) {
    yield put(flyersActions.setCurrentFlyerGibs({ [flyer.id]: [] }, {}));
    // trackFlyerGib waits for flyerGibs RESET: without it, tracking would hang forever.
    yield put(flyerGibsActions.resetFlyerGibs({ list: {} }));
    return null;
  }

  const flyerGibsResource = yield call(fetchFlyerGibsResource, flyer.id);
  if (flyerGibsResource) {
    const flyerGibsMap = flyerGibsResource.reduce((map, gib) => {
      map[gib.id] = gib;
      return map;
    }, {});

    const flyerGibsList = { [flyer.id]: sortFlyerGibIdsByFlyerOrder(Object.values(flyerGibsMap)) };
    const flyerGibsChildIdsMap = getFlyerGibsChildIdsMap(flyerGibsMap);

    yield put(flyersActions.setCurrentFlyerGibs(flyerGibsList, flyerGibsChildIdsMap));
    yield put(flyerGibsActions.resetFlyerGibs({ list: flyerGibsMap }));
  }
}

export function* onFetchCurrentFlyerGib(flyer, tagId) {
  const showFlyerGibs = yield select(propertySelectors.selectShowFlyerGibs);
  if (!showFlyerGibs) return;

  const flyerGibsMap = yield select(flyerGibsSelectors.selectFlyerGibs);

  const flyerGibsProductResource = yield call(fetchFlyerGibsProductResource, flyer.id, tagId);

  if (flyerGibsProductResource && flyerGibsMap[tagId]) {
    flyerGibsMap[tagId].product = flyerGibsProductResource;
    yield put(tagsActions.setSelectedVariantIndex(tagId, null));
  }

  const flyerGibsList = { [flyer.id]: sortFlyerGibIdsByFlyerOrder(Object.values(flyerGibsMap)) };
  const flyerGibsChildIdsMap = getFlyerGibsChildIdsMap(flyerGibsMap);
  yield put(flyersActions.setCurrentFlyerGibs(flyerGibsList, flyerGibsChildIdsMap));
  yield put(flyerGibsActions.resetFlyerGibs({ list: flyerGibsMap }));
}

function* fetchFlyerIfNeeded({ flyer, flyerId, location, storeCustomLabel }) {
  if (flyer && flyer.id === flyerId && !storeCustomLabel) {
    return flyer;
  }

  try {
    return yield call(fetchFlyerResource, { flyerId, location, storeCustomLabel });
  } catch (error) {
    return null;
  }
}

/** Resolves PDF/NEXT links, then fetches + navigates if URL `defaultFlyerVersion` requires another flyer id. */
function* alignFlyerToQueryDefaultVersion({ flyer, location, storeCustomLabel }) {
  const defaultFlyerVersion = yield select(routerSelectors.selectDefaultFlyerVersion);
  const retailerId = yield select(routerSelectors.selectRetailerId);
  let resolution;

  try {
    resolution = yield call(resolveFlyerVersionLinks, flyer, storeCustomLabel, retailerId);
    flyer = mergeFlyerVersionResolution(flyer, resolution);
  } catch (e) {
    // Keep flyer as fetched when customResources resolution fails
  }

  const routerLocation = yield select(routerSelectors.selectRouterLocation);
  const originURL = yield select(uiSelectors.selectOriginURL);
  const skipDefaultAlign =
    routerLocation?.state?.[SKIP_DEFAULT_FLYER_VERSION_ALIGN_STATE_KEY] === true;
  // A Next product share already opened this flyer. Do not replace it with the PDF id
  // when the publisher page still sends defaultFlyerVersion=PDF.
  const keepSharedNextFlyer = isSharedNextFlyer(flyer?.id, originURL);
  const targetFlyerId = keepSharedNextFlyer
    ? null
    : getTargetFlyerIdForDefaultVersion({
        flyer,
        resolution,
        defaultFlyerVersion,
        skipDefaultAlign,
      });

  if (!targetFlyerId) {
    return { flyer, versionReplaced: false };
  }

  try {
    const resolved = yield call(fetchFlyerResource, {
      flyerId: targetFlyerId,
      location,
      storeCustomLabel,
    });
    if (!resolved?.id) {
      return { flyer, versionReplaced: false };
    }

    const match = yield select(routerSelectors.routerMatchPath, flyerPath);
    const flyerPage = (match && match.params && match.params.flyerPage) || '1';
    const resolvedWithLinks = mergeClientVersionLinksAndFlagsOntoFlyer(flyer, resolved);

    yield put(flyersActions.setCurrentFlyer(resolvedWithLinks));
    yield call(onFetchFlyerGibs, resolvedWithLinks);
    yield put(replace(createLinkPath(flyerPath, { flyerId: resolvedWithLinks.id, flyerPage })));

    return { flyer: resolvedWithLinks, versionReplaced: true };
  } catch (e) {
    // Keep showing `flyer` when the alternate id cannot be loaded
    return { flyer, versionReplaced: false };
  }
}

/** When URL has storeId / store code, set user geolocation to that store if it exists on the flyer. */
function* applyUserGeolocationFromMatchingFlyerStore(flyer, storeCustomLabel) {
  if (!flyer?.stores || !storeCustomLabel) return;

  const store = Object.values(flyer.stores).find(
    (s) => s.extras.customer_code === storeCustomLabel,
  );
  if (store) {
    yield put(userActions.setGeolocation({ label: store.city, lat: store.lat, lng: store.lng }));
  }
}

export function* onFetchCurrentFlyer({ payload }) {
  const location = yield select(userSelector.selectNearbyGeolocation);
  const storeCustomLabel = yield select(routerSelectors.selectStoreId);
  const { flyerId, nearestStoreId, tagId } = payload;

  //TODO[GB] da verificare che se con l api
  const flyerFromList = yield select(flyersSelectors.selectFlyer, flyerId);
  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer);

  let flyer = flyerFromList ?? currentFlyer;

  flyer = yield call(fetchFlyerIfNeeded, { flyer, flyerId, location, storeCustomLabel });
  // The flyer does not exist or is expired: show the unavailable error instead of the endless loader
  if (!flyer) {
    yield put(flyersActions.setCurrentFlyerNotFound(flyerId));
    return {};
  }

  flyer = copyClientVersionExtrasFromPriorIfSamePromo(currentFlyer, flyer);

  // id flyer?.id not exists redirect to flyers page
  if (!flyer?.id) {
    const redirectFlyersPath = createLinkPath(flyersPath);
    yield put(replace(redirectFlyersPath));
    return;
  }

  // Dual-format (PDF / NEXT): merge promo_code + `_next` resolved ids, then align with defaultFlyerVersion
  const defaultVersionAlignment = yield call(alignFlyerToQueryDefaultVersion, {
    flyer,
    location,
    storeCustomLabel,
  });
  flyer = defaultVersionAlignment.flyer;
  const versionReplaced = defaultVersionAlignment.versionReplaced;

  // set the position on storeCustomLabel if in url exists storeCustomLabel and found it in flyer
  yield call(applyUserGeolocationFromMatchingFlyerStore, flyer, storeCustomLabel);

  if (!versionReplaced) {
    if (currentFlyer?.id != flyerId) {
      // eslint-disable-line eqeqeq
      yield put(flyersActions.setCurrentFlyer(flyer));
    }
    yield call(onFetchFlyerGibs, flyer);
  }

  // route /flyer/:flyerId/stores/:nearestStoreId
  if (nearestStoreId) {
    yield call(onFetchCurrentFlyerStores, flyer, location);
  }

  // route /flyer/:flyerId/tags
  if (tagId) {
    yield call(onFetchCurrentFlyerGib, flyer, tagId);
  }

  yield spawn(checkGeolocationAlert);
  // yield spawn(checkCurrentFlyerStores);
}

// export function* onSetCurrentFlyer({ payload }) {
//   // yield fork(fetchFlyerContents, payload.flyerId);
//   yield call(onFetchCurrentFlyer, payload.flyerId);
//   yield spawn(checkGeolocationAlert);
//   yield spawn(checkCurrentFlyerStores);
// }

export function* onStoresReset() {
  // yield spawn(checkCurrentFlyerStores);
}

export function* onSetCurrentStore() {
  yield call(updateFlyers);
  yield spawn(setSessionOnPropertty);
  // yield spawn(checkCurrentFlyerStores);
}

export function* onRouterLocationChange() {
  if (!setGeolocationChanged) {
    // TODO[GB] eseguo solo se non é stato eseguito da onSetGeolocation
    setGeolocationChanged = true;
    yield call(updateFlyers);
  }

  yield spawn(setSessionOnPropertty);
}

export function* fetchFlyerStoresOpeningHours({ payload }) {
  const { flyerId, nearestStoreId } = payload;

  const flyerFromList = yield select(flyersSelectors.selectFlyer, flyerId);
  const currentFlyer = yield select(flyersSelectors.selectCurrentFlyer);

  let flyer = flyerFromList ?? currentFlyer;

  if (!flyer?.stores?.list?.[nearestStoreId]?.storeHours) {
    try {
      const response = yield call(
        fetchStoreOpeningHoursResource,
        flyer.settings.parent_id ?? flyer.id,
        nearestStoreId,
      );

      yield put(flyersActions.setCurrentFlyerStoreOpeningInfos(nearestStoreId, response));
      yield put(storesActions.updateStoreOpeningInfos(nearestStoreId, response));
    } catch (error) {
      console.error(error);
    }
  }
}

export function* fetchFlyerStoresForShowMore({ payload }) {
  const { flyerId, parentFlyerId } = payload;
  const location = yield select(userSelector.selectNearbyGeolocation);
  const storeCustomLabel = yield select(routerSelectors.selectStoreId);

  try {
    const response = yield call(fetchStoresResource, {
      flyerId,
      location,
      limit: 3,
      storeCustomLabel,
      parentFlyerId,
    });

    const transformedResponse = response.reduce((acc, store) => {
      acc[store.id] = store;
      return acc;
    }, {});

    yield put(flyersActions.setFlyerStores(flyerId, response, 'partial'));
    yield put(
      storesActions.updateStoresByLocation({
        list: { [flyerId]: { stores: transformedResponse } },
      }),
    );
  } catch (error) {
    console.error(error);
  }
}

// export function* onFetchFlyerStores() {
//   const { flyerId, storeId } = payload;
//   if (!storeId) {
//     yield call(fetchFlyerStoresForShowMore, flyerId);
//   } else {
//     // yield call(fetchFlyerStoresForStoresPage, flyerId, storeId);
//   }
// }

let setGeolocationChanged = false;

export default function* () {
  yield takeEvery(LOCATION_CHANGE, onRouterLocationChange);
  yield takeEvery(userActions.types.SET_GEOLOCATION, onSetGeolocation);
  yield takeEvery(storesActions.types.RESET, onStoresReset);
  yield takeEvery(routerActions.types.UPDATE_GEOLOCATION, onRouterUpdateGeolocation);
  yield takeEvery(storesActions.types.SET_CURRENT_STORE, onSetCurrentStore);
  yield takeEvery(flyersActions.types.FETCH_CURRENT_FLYER, onFetchCurrentFlyer);
  yield takeEvery(flyersActions.types.FETCH_FLYER_STORES, fetchFlyerStoresForShowMore);
  yield takeEvery(
    flyersActions.types.FETCH_CURRENT_FLYER_STORE_OPENING_INFOS,
    fetchFlyerStoresOpeningHours,
  );
}
